import { Component, computed, effect, inject, input, resource, signal } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_SVGICON } from '@progress/kendo-angular-icons';
import { KENDO_FORMFIELD, KENDO_TEXTBOX } from '@progress/kendo-angular-inputs';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { KENDO_PROGRESSBAR } from '@progress/kendo-angular-progressbar';
import { FileInfo, FileRestrictions, SelectEvent, UploadsModule } from '@progress/kendo-angular-upload';
import { UploadsClient } from '@unity/clients';
import { ControlWrapperComponent } from '@unity/forms/composed/control-wrapper/control-wrapper.component';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'unity-file-uploader-control',
  standalone: true,
  imports: [
    KENDO_FORMFIELD,
    KENDO_LABEL,
    KENDO_TEXTBOX,
    KENDO_SVGICON,
    KENDO_BUTTON,
    KENDO_PROGRESSBAR,
    UploadsModule,
    ReactiveFormsModule,
    ControlWrapperComponent,
  ],
  templateUrl: './file-uploader-control.component.html',
  styleUrl: './file-uploader-control.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: FileUploaderControlComponent,
    },
  ],
})
export class FileUploaderControlComponent {
  protected readonly uploadsClient = inject(UploadsClient);

  public readonly $control = input.required<FormControl>({ alias: 'control' });
  public readonly $displayName = input.required<string>({ alias: 'displayName' });
  public readonly $description = input.required<string>({ alias: 'description' });
  public readonly $allowedExtensions = input.required<string[]>({ alias: 'allowedExtensions' });
  public readonly $currentFileName = input<string>(undefined, { alias: 'currentFileName' });
  public readonly $showHint = input<boolean>(undefined, { alias: 'showHint' });

  protected readonly $file = signal<FileInfo | undefined>(undefined);

  protected readonly $fileRestrictions = computed((): FileRestrictions => {
    const allowedExtensions = this.$allowedExtensions();
    return {
      allowedExtensions: allowedExtensions.length == 0 ? undefined : allowedExtensions,
    };
  });

  protected readonly $accept = computed(() => {
    const allowedExtensions = this.$allowedExtensions();
    return allowedExtensions.join(',');
  });

  protected createResource = resource({
    params: () => ({ file: this.$file() }),
    loader: async ({ params: { file } }) => {
      if (!file) {
        return;
      }

      return firstValueFrom(
        this.uploadsClient.createOne({
          file_name: file.name,
          file_size: file.size ?? 0,
          file_type: file.rawFile?.type ?? '',
        })
      );
    },
  });

  protected updateResource = resource({
    params: () => ({ file: this.$file(), create: this.createResource.value() }),
    loader: async ({ params: { file, create } }) => {
      if (!file || !create) {
        return;
      }

      return firstValueFrom(
        this.uploadsClient.updateOne(create.id, {
          data: file.rawFile,
          fileName: file.name,
        })
      );
    },
  });

  protected readonly $isLoading = computed(() => {
    return this.createResource.isLoading() && this.updateResource.isLoading();
  });

  public constructor() {
    effect(() => {
      const control = this.$control();
      const file = this.$file();

      if (file) {
        control.reset();
      }
    });

    effect(() => {
      const control = this.$control();
      const create = this.createResource.value();
      const updatehasValue = this.updateResource.hasValue();

      if (updatehasValue && create) {
        control.setValue(create.id);
      }
    });
  }

  protected onSelect(event: SelectEvent) {
    this.$file.set(event.files[0]);
  }

  protected onRemove() {
    this.$control().reset();
  }
}
